import { type IGetRecordResponse } from '@principle-theorem/practice-migrations/interfaces';
import {
  ICustomFormDataResolverConfigExtended,
  setCustomFormValue,
} from '../../mappings/allergy-to-medical-history';
import { type PraktikaMedicalAllergy } from '../source/entities/patient-medical-allergy';
import { type PraktikaMedicalHistory } from '../source/entities/patient-medical-history';
import {
  MedicalHistoryFormPath,
  parseMedicalHistoryEnumValue,
  PRAKTIKA_ALLERGY_BOOLEAN_MAPPING,
  PRAKTIKA_ALLERGY_LABELS,
  PRAKTIKA_MEDICAL_HISTORY_BOOLEAN_MAPPING,
  PRAKTIKA_MEDICAL_HISTORY_ENUM_MAPPING,
  PRAKTIKA_MEDICAL_HISTORY_OTHER_LABELS,
  PRAKTIKA_MEDICAL_HISTORY_STRING_MAPPING,
} from './medical-history';

function setFormValueByPath(
  formData: object,
  formOptions: ICustomFormDataResolverConfigExtended[],
  path: string,
  booleanValue: boolean,
  associatedValue?: string,
  stringValue?: string
): object {
  const formOption = formOptions.find((option) => option.path === path);
  if (!formOption) {
    return formData;
  }
  return setCustomFormValue(
    formOption,
    formData,
    booleanValue,
    associatedValue,
    stringValue
  );
}

function appendOtherLine(
  lines: string[],
  labels: Record<string, string>,
  itemId: string,
  value: string | null
): void {
  const label = labels[itemId] ?? itemId;
  const line = value ? `${label}: ${value}` : label;
  lines.push(line);
}

/**
 * Build the medical-history form data object from Praktika history rows.
 *
 * Each row is handled by exactly one branch:
 * - Enum-mapped codes set their enum field.
 * - Boolean and/or string mapped codes set their respective fields (a code may
 *   appear in both mappings, e.g. SMOKER sets `isSmoker` and `amountSmoked`).
 * - Only codes with no explicit mapping are accumulated into conditions.other.
 *
 * A mapped row never leaks into conditions.other.
 */
export function processMedicalHistoryRows(
  historyRows: IGetRecordResponse<PraktikaMedicalHistory>[],
  formOptions: ICustomFormDataResolverConfigExtended[]
): object {
  let formData = {};
  const otherConditionLines: string[] = [];

  for (const row of historyRows) {
    const { item_id: itemId, is_present: isPresent, value } = row.data;

    const enumPath = PRAKTIKA_MEDICAL_HISTORY_ENUM_MAPPING[itemId];
    if (enumPath) {
      const enumValue = isPresent
        ? parseMedicalHistoryEnumValue(itemId, value)
        : undefined;
      if (enumValue) {
        formData = setFormValueByPath(
          formData,
          formOptions,
          enumPath,
          true,
          enumValue
        );
        continue;
      }
      // Absent rows are dropped; present rows whose value can't be resolved to
      // a known enum option fall through to conditions.other so the raw value
      // is preserved rather than guessed.
      if (!isPresent) {
        continue;
      }
    }

    const booleanPath = PRAKTIKA_MEDICAL_HISTORY_BOOLEAN_MAPPING[itemId];
    const stringPath = PRAKTIKA_MEDICAL_HISTORY_STRING_MAPPING[itemId];
    if (booleanPath || stringPath) {
      if (booleanPath && isPresent) {
        formData = setFormValueByPath(formData, formOptions, booleanPath, true);
      }
      if (stringPath && value) {
        formData = setFormValueByPath(
          formData,
          formOptions,
          stringPath,
          false,
          undefined,
          value
        );
      }
      continue;
    }

    if (isPresent || value) {
      appendOtherLine(
        otherConditionLines,
        PRAKTIKA_MEDICAL_HISTORY_OTHER_LABELS,
        itemId,
        value
      );
    }
  }

  if (otherConditionLines.length) {
    formData = setFormValueByPath(
      formData,
      formOptions,
      MedicalHistoryFormPath.ConditionsOther,
      false,
      undefined,
      otherConditionLines.join('; ')
    );
  }

  return formData;
}

/**
 * Augment the form data with Praktika allergy rows.
 *
 * Boolean-mapped allergy codes set their field; only unmapped codes are
 * accumulated into allergies.other. A mapped row never leaks into the other
 * free-text field.
 */
export function processAllergyRows(
  allergyRows: IGetRecordResponse<PraktikaMedicalAllergy>[],
  formData: object,
  formOptions: ICustomFormDataResolverConfigExtended[]
): object {
  let updatedData = formData;
  const otherAllergyLines: string[] = [];

  for (const row of allergyRows) {
    const { item_id: itemId, is_present: isPresent, value } = row.data;

    const booleanPath = PRAKTIKA_ALLERGY_BOOLEAN_MAPPING[itemId];
    if (booleanPath) {
      if (isPresent) {
        updatedData = setFormValueByPath(
          updatedData,
          formOptions,
          booleanPath,
          true
        );
      }
      continue;
    }

    if (isPresent || value) {
      appendOtherLine(
        otherAllergyLines,
        PRAKTIKA_ALLERGY_LABELS,
        itemId,
        value
      );
    }
  }

  if (otherAllergyLines.length) {
    updatedData = setFormValueByPath(
      updatedData,
      formOptions,
      MedicalHistoryFormPath.AllergiesOther,
      false,
      undefined,
      otherAllergyLines.join('; ')
    );
  }

  return updatedData;
}
